import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon, DeleteIcon, EditIcon, LockIcon, SearchIcon } from "@chakra-ui/icons";
import PageHeader from "../components/PageHeader";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  getPsActiveSeason,
  getPsPool,
  getPsEligiblePlayers,
  getMyPsEntries,
  createPsEntry,
  editPsEntry,
  deletePsEntry,
} from "../api/mlbPlayoff";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";
const ENTRY_SIZE   = 6;
const TABLE_PAGE_SIZE = 50;

const MLB_ABBREVS = {
  "Arizona Diamondbacks": "ARI", "Atlanta Braves": "ATL", "Baltimore Orioles": "BAL",
  "Boston Red Sox": "BOS", "Chicago Cubs": "CHC", "Chicago White Sox": "CWS",
  "Cincinnati Reds": "CIN", "Cleveland Guardians": "CLE", "Colorado Rockies": "COL",
  "Detroit Tigers": "DET", "Houston Astros": "HOU", "Kansas City Royals": "KC",
  "Los Angeles Angels": "LAA", "Los Angeles Dodgers": "LAD", "Miami Marlins": "MIA",
  "Milwaukee Brewers": "MIL", "Minnesota Twins": "MIN", "New York Mets": "NYM",
  "New York Yankees": "NYY", "Oakland Athletics": "OAK", "Athletics": "ATH",
  "Sacramento Athletics": "ATH", "Philadelphia Phillies": "PHI", "Pittsburgh Pirates": "PIT",
  "San Diego Padres": "SD", "San Francisco Giants": "SF", "Seattle Mariners": "SEA",
  "St. Louis Cardinals": "STL", "Tampa Bay Rays": "TB", "Texas Rangers": "TEX",
  "Toronto Blue Jays": "TOR", "Washington Nationals": "WSH",
};

function teamAbbr(name) {
  if (!name) return "—";
  if (MLB_ABBREVS[name]) return MLB_ABBREVS[name];
  if (name.length <= 4) return name;
  return name.substring(0, 3).toUpperCase();
}

const PlayerBrowserPanel = React.memo(function PlayerBrowserPanel({
  players, totalCount, selectedIdsSet, selectedIdsLength, lockedIds,
  sortKey, sortDir, searchQuery, page, totalPages, height,
  onTogglePlayer, onRemovePlayer, onToggleSort, onSearchChange, onPageChange,
  entriesOpen, editMode,
}) {
  function sortInd(key) {
    if (sortKey !== key) return " ↕";
    return sortDir === "desc" ? " ↓" : " ↑";
  }

  const headerLabel = editMode
    ? "Eligible Players (editable slots only)"
    : entriesOpen
    ? "Eligible Players"
    : "Eligible Players (entries closed)";

  return (
    <Box
      flex="3"
      minW="0"
      bg="white"
      color="black"
      border={`3px solid ${TOP_BAR_BLUE}`}
      borderRadius="lg"
      boxShadow="lg"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      height={height ? `${height}px` : undefined}
      maxH={height ? undefined : "80vh"}
    >
      <Box px={6} pt={5} pb={3} flexShrink={0} borderBottom="2px solid" borderColor="gray.200">
        <HStack justify="space-between" align="center">
          <Heading size="md" color={TOP_BAR_BLUE}>{headerLabel}</Heading>
          <InputGroup size="sm" maxW="260px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search name or team..."
              value={searchQuery}
              onChange={onSearchChange}
              bg="gray.50"
              borderColor="gray.200"
            />
          </InputGroup>
        </HStack>
      </Box>

      <Box flex="1" overflowX="auto" overflowY="auto">
        <Table size="sm" variant="simple"
               sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <Thead position="sticky" top={0} bg="white" zIndex={1} boxShadow="0 1px 0 #e2e8f0">
            <Tr>
              {/* Frozen so the name stays readable while the stats scroll
                  under it on a phone. Needs its own background and a z-index
                  above both the sticky header and the sticky body cells. */}
              <Th cursor="pointer" userSelect="none" w="180px" position="sticky" left={0}
                  zIndex={3} bg="white"
                  onClick={() => onToggleSort("name")} color={sortKey === "name" ? TOP_BAR_BLUE : undefined}>Player{sortInd("name")}</Th>
              <Th cursor="pointer" userSelect="none" w="80px" onClick={() => onToggleSort("team")} color={sortKey === "team" ? TOP_BAR_BLUE : undefined}>Team{sortInd("team")}</Th>
              <Th cursor="pointer" userSelect="none" w="52px" onClick={() => onToggleSort("position")} color={sortKey === "position" ? TOP_BAR_BLUE : undefined}>Pos{sortInd("position")}</Th>
              <Th isNumeric cursor="pointer" userSelect="none" w="48px" onClick={() => onToggleSort("reg_games")} color={sortKey === "reg_games" ? TOP_BAR_BLUE : undefined}>G{sortInd("reg_games")}</Th>
              <Th isNumeric cursor="pointer" userSelect="none" w="48px" onClick={() => onToggleSort("reg_ab")} color={sortKey === "reg_ab" ? TOP_BAR_BLUE : undefined}>AB{sortInd("reg_ab")}</Th>
              <Th isNumeric cursor="pointer" userSelect="none" w="48px" onClick={() => onToggleSort("reg_hr")} color={sortKey === "reg_hr" ? TOP_BAR_BLUE : undefined}>HR{sortInd("reg_hr")}</Th>
              <Th isNumeric cursor="pointer" userSelect="none" w="64px" onClick={() => onToggleSort("reg_avg")} color={sortKey === "reg_avg" ? TOP_BAR_BLUE : undefined}>AVG{sortInd("reg_avg")}</Th>
              <Th isNumeric cursor="pointer" userSelect="none" w="64px" onClick={() => onToggleSort("reg_slg")} color={sortKey === "reg_slg" ? TOP_BAR_BLUE : undefined}>SLG{sortInd("reg_slg")}</Th>
              <Th w="60px"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {players.length === 0 ? (
              <Tr>
                {/* Nine columns, including the action one at the end. */}
                <Td colSpan={9}>
                  <Text color="gray.400" py={4} textAlign="center">
                    {entriesOpen || editMode ? "No players found." : "Entries are closed — all playoff teams have already played."}
                  </Text>
                </Td>
              </Tr>
            ) : players.map((player) => {
              const isSelected = selectedIdsSet.has(player.player_id);
              const isLocked   = lockedIds.has(player.player_id);
              const canAct     = entriesOpen || (editMode && !isLocked);
              const isFull     = selectedIdsSet.size >= ENTRY_SIZE && !isSelected;
              const isDisabled = isFull || !canAct || isLocked;
              const rowBg   = isSelected ? "blue.50" : "white";
              const hoverBg = isSelected ? "blue.100" : isDisabled ? "white" : "gray.50";
              return (
                <Tr
                  key={player.player_id}
                  bg={rowBg}
                  _hover={{ bg: hoverBg, "& > td.ps-sticky-col": { bg: hoverBg } }}
                  cursor={isDisabled ? "default" : "pointer"}
                  opacity={isFull && !isSelected ? 0.45 : 1}
                  onClick={() => !isDisabled && onTogglePlayer(player)}
                >
                  <Td className="ps-sticky-col" position="sticky" left={0} zIndex={2}
                      bg={rowBg}
                      fontWeight={isSelected || sortKey === "name" ? "bold" : "medium"} color={!isSelected && sortKey === "name" ? TOP_BAR_BLUE : undefined}>{player.name}</Td>
                  <Td fontWeight={sortKey === "team" ? "bold" : "semibold"} fontSize="xs" color={sortKey === "team" ? TOP_BAR_BLUE : undefined}>{teamAbbr(player.team)}</Td>
                  <Td fontSize="xs" color={sortKey === "position" ? TOP_BAR_BLUE : "gray.500"} fontWeight={sortKey === "position" ? "bold" : "normal"}>{player.position || "—"}</Td>
                  <Td isNumeric fontWeight={sortKey === "reg_games" ? "bold" : "normal"} color={sortKey === "reg_games" ? TOP_BAR_BLUE : undefined}>{player.reg_games ?? 0}</Td>
                  <Td isNumeric fontWeight={sortKey === "reg_ab" ? "bold" : "normal"} color={sortKey === "reg_ab" ? TOP_BAR_BLUE : undefined}>{player.reg_ab ?? 0}</Td>
                  <Td isNumeric fontWeight={sortKey === "reg_hr" ? "bold" : "normal"} color={sortKey === "reg_hr" ? TOP_BAR_BLUE : undefined}>{player.reg_hr ?? 0}</Td>
                  <Td isNumeric fontWeight={sortKey === "reg_avg" ? "bold" : "normal"} color={sortKey === "reg_avg" ? TOP_BAR_BLUE : undefined}>{player.reg_avg != null ? player.reg_avg.toFixed(3) : "—"}</Td>
                  <Td isNumeric fontWeight={sortKey === "reg_slg" ? "bold" : "normal"} color={sortKey === "reg_slg" ? TOP_BAR_BLUE : undefined}>{player.reg_slg != null ? player.reg_slg.toFixed(3) : "—"}</Td>
                  <Td>
                    {isSelected ? (
                      <Button size="xs" colorScheme="red" variant="outline"
                        isDisabled={isLocked}
                        onClick={(e) => { e.stopPropagation(); !isLocked && onRemovePlayer(player.player_id); }}>
                        Remove
                      </Button>
                    ) : (
                      <Button size="xs" colorScheme="blue" variant="outline"
                        isDisabled={isDisabled}
                        onClick={(e) => { e.stopPropagation(); !isDisabled && onTogglePlayer(player); }}>
                        Add
                      </Button>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      <Box px={4} py={2} bg="gray.50" borderTop="1px solid" borderColor="gray.100" flexShrink={0}>
        <HStack justify="space-between">
          {/* No "default sort" note: the list opens sorted by name. */}
          <Text fontSize="xs" color="gray.500">{totalCount} player{totalCount !== 1 ? "s" : ""} · Regular season stats</Text>
          {totalPages > 1 && (
            <HStack spacing={1}>
              <Button size="xs" variant="ghost" isDisabled={page === 1} onClick={() => onPageChange(1)}>«</Button>
              <Button size="xs" variant="ghost" isDisabled={page === 1} onClick={() => onPageChange(page - 1)}>‹</Button>
              <Text fontSize="xs" color="gray.600" px={1}>{page} / {totalPages}</Text>
              <Button size="xs" variant="ghost" isDisabled={page === totalPages} onClick={() => onPageChange(page + 1)}>›</Button>
              <Button size="xs" variant="ghost" isDisabled={page === totalPages} onClick={() => onPageChange(totalPages)}>»</Button>
            </HStack>
          )}
        </HStack>
      </Box>
    </Box>
  );
});

export default function MlbPlayoffDraftPage() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const { poolId }       = useParams();
  const numericPoolId    = Number(poolId);

  const [season,           setSeason]           = useState(null);
  const [eligiblePlayers,  setEligiblePlayers]  = useState([]);
  const [allEntryPlayers,  setAllEntryPlayers]  = useState([]);  // combined for edit mode
  const [entriesOpen,      setEntriesOpen]      = useState(true);
  const [loadingPage,      setLoadingPage]      = useState(true);
  const [pageError,        setPageError]        = useState("");
  const [existingEntries,  setExistingEntries]  = useState([]);

  // Edit state
  const [editEntryId,     setEditEntryId]     = useState(null);
  const [lockedPlayerIds, setLockedPlayerIds] = useState(new Set());
  const [deletingEntryId, setDeletingEntryId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const draftFormRef = useRef(null);

  const [teamName,      setTeamName]      = useState("");
  const [selectedIds,   setSelectedIds]   = useState([]);
  const [submitting,    setSubmitting]    = useState(false);
  const [submitError,   setSubmitError]   = useState("");
  const [justSubmitted, setJustSubmitted] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey,     setSortKey]     = useState("name");
  const [sortDir,     setSortDir]     = useState("asc");
  const [page,        setPage]        = useState(1);

  const leftColRef      = useRef(null);
  const [leftColHeight, setLeftColHeight] = useState(null);

  // Below xl the body Flex wraps to one column, which put the player list
  // under Your Entries — a long scroll away from the form it feeds. Stacked,
  // it goes directly under the draft form instead. Same element either way,
  // mounted in one place or the other.
  const stackPanel = useBreakpointValue({ base: true, xl: false }) ?? true;

  useLayoutEffect(() => {
    if (loadingPage) return;
    const el = leftColRef.current;
    if (!el) return;
    const update = () => setLeftColHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loadingPage]);

  useEffect(() => {
    async function load() {
      try {
        const s = await getPsActiveSeason();
        setSeason(s);
        // The pool fetch's result is unused, but the call is kept: it rejects
        // for non-members, so it doubles as the page's access check.
        const [, players, entries] = await Promise.all([
          getPsPool(s.id, numericPoolId),
          getPsEligiblePlayers(s.id),
          getMyPsEntries(s.id, numericPoolId),
        ]);
        setEligiblePlayers(players);
        // Asked of the season, not inferred from the list being empty. An
        // empty list has several causes, and reading every one of them as
        // "entries are closed" told players the draft had shut when it had
        // not even opened.
        setEntriesOpen(s.entries_open !== false);
        setExistingEntries(entries);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Unable to load page.";
        setPageError(typeof msg === "string" ? msg : "Unable to load page.");
      } finally {
        setLoadingPage(false);
      }
    }
    load();
  }, [numericPoolId]);

  const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // In edit mode: combine eligible players with locked players from the entry
  // so locked players still appear in the panel
  const displayPool = useMemo(() => {
    if (!editEntryId || lockedPlayerIds.size === 0) return eligiblePlayers;
    // Make sure locked players appear even if their team has played (not in eligible list)
    const eligible = [...eligiblePlayers];
    const eligibleIds = new Set(eligible.map((p) => p.player_id));
    const lockedFromEntry = allEntryPlayers.filter(
      (p) => lockedPlayerIds.has(p.player_id) && !eligibleIds.has(p.player_id)
    );
    return [...lockedFromEntry, ...eligible];
  }, [eligiblePlayers, allEntryPlayers, editEntryId, lockedPlayerIds]);

  const selectedPlayers = useMemo(
    () => selectedIds.map((id) => displayPool.find((p) => p.player_id === id)).filter(Boolean),
    [selectedIds, displayPool]
  );

  const displayedPlayers = useMemo(() => {
    let list = [...displayPool];
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.team || "").toLowerCase().includes(q) ||
          (p.position || "").toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => {
      if (sortKey === "name") {
        const cmp = a.name.localeCompare(b.name);
        return sortDir === "desc" ? -cmp : cmp;
      }
      if (sortKey === "team") {
        const cmp = (a.team || "").localeCompare(b.team || "");
        return sortDir === "desc" ? -cmp : cmp;
      }
      if (sortKey === "position") {
        const cmp = (a.position || "").localeCompare(b.position || "");
        return sortDir === "desc" ? -cmp : cmp;
      }
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      return sortDir === "desc" ? bVal - aVal : aVal - bVal;
    });
  }, [displayPool, searchQuery, sortKey, sortDir]);

  useEffect(() => setPage(1), [displayedPlayers]);

  const pagedPlayers = useMemo(() => {
    const start = (page - 1) * TABLE_PAGE_SIZE;
    return displayedPlayers.slice(start, start + TABLE_PAGE_SIZE);
  }, [displayedPlayers, page]);

  const totalPages = Math.max(1, Math.ceil(displayedPlayers.length / TABLE_PAGE_SIZE));

  const handleTogglePlayer = useCallback((player) => {
    if (lockedPlayerIds.has(player.player_id)) return;
    setSelectedIds((prev) => {
      if (prev.includes(player.player_id)) return prev.filter((id) => id !== player.player_id);
      if (prev.length >= ENTRY_SIZE) return prev;
      return [...prev, player.player_id];
    });
    setSubmitError("");
  }, [lockedPlayerIds]);

  const handleRemovePlayer = useCallback((id) => {
    if (lockedPlayerIds.has(id)) return;
    setSelectedIds((prev) => prev.filter((pid) => pid !== id));
    setSubmitError("");
  }, [lockedPlayerIds]);

  const handleToggleSort = useCallback((key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "desc" ? "asc" : "desc"));
        return prev;
      }
      setSortDir("desc");
      return key;
    });
  }, []);

  const handleSearchChange = useCallback((e) => setSearchQuery(e.target.value), []);
  const handlePageChange   = useCallback((p) => setPage(p), []);

  async function handleSubmit() {
    setSubmitError("");
    if (!teamName.trim()) { setSubmitError("Team name is required."); return; }
    if (selectedIds.length !== ENTRY_SIZE) { setSubmitError(`Select exactly ${ENTRY_SIZE} players.`); return; }

    setSubmitting(true);
    try {
      if (editEntryId) {
        await editPsEntry(season.id, numericPoolId, editEntryId, {
          team_name: teamName.trim(),
          player_ids: selectedIds,
        });
        setJustSubmitted({ name: teamName.trim(), action: "updated" });
        handleCancelEdit();
      } else {
        await createPsEntry(season.id, numericPoolId, {
          team_name: teamName.trim(),
          player_ids: selectedIds,
        });
        setJustSubmitted({ name: teamName.trim(), action: "submitted" });
        setTeamName("");
        setSelectedIds([]);
      }
      const updated = await getMyPsEntries(season.id, numericPoolId);
      setExistingEntries(updated);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to submit entry.";
      setSubmitError(typeof msg === "string" ? msg : "Failed to submit entry.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleLoadForEdit(entry) {
    // A player is locked if their team has played its first game.
    // The eligible list only contains players from teams with first_game_played_at IS NULL,
    // so any entry player absent from the eligible list belongs to a team that has played.
    const locked = new Set();
    const entryPlayers = entry.players || [];
    const eligibleIdSet = new Set(eligiblePlayers.map((ep) => ep.player_id));
    entryPlayers.forEach((p) => {
      if (!eligibleIdSet.has(p.player_id)) locked.add(p.player_id);
    });

    // Store full player data for locked players (they won't be in eligiblePlayers)
    // Convert entry players to the same format as eligiblePlayers
    const entryPlayerData = entryPlayers.map((p) => ({
      player_id: p.player_id,
      name: p.name,
      team: p.team,
      position: p.position,
      total_bases: p.total_bases ?? 0,
      home_runs: p.home_runs ?? 0,
      hits: p.hits ?? 0,
      at_bats: p.at_bats ?? 0,
      singles: p.singles ?? 0,
      doubles: p.doubles ?? 0,
      triples: p.triples ?? 0,
    }));

    setAllEntryPlayers(entryPlayerData);
    setLockedPlayerIds(locked);
    setEditEntryId(entry.entry_id);
    setTeamName(entry.team_name);
    setSelectedIds(entryPlayers.map((p) => p.player_id));
    setSubmitError("");
    setJustSubmitted(null);
    setConfirmDeleteId(null);
    draftFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function handleCancelEdit() {
    setEditEntryId(null);
    setLockedPlayerIds(new Set());
    setAllEntryPlayers([]);
    setTeamName("");
    setSelectedIds([]);
    setSubmitError("");
  }

  async function handleDeleteEntry(entryId) {
    setDeletingEntryId(entryId);
    try {
      await deletePsEntry(season.id, numericPoolId, entryId);
      const updated = await getMyPsEntries(season.id, numericPoolId);
      setExistingEntries(updated);
      if (editEntryId === entryId) handleCancelEdit();
    } catch (err) {
      setPageError(err?.response?.data?.detail || "Failed to delete entry.");
    } finally {
      setDeletingEntryId(null);
      setConfirmDeleteId(null);
    }
  }

  function handleLogout() { logout(); navigate("/login", { replace: true }); }

  if (loadingPage) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" />
          <Text>Loading draft page...</Text>
        </Flex>
      </Box>
    );
  }

  const editMode = !!editEntryId;
  const canSubmit = editMode
    ? selectedIds.length === ENTRY_SIZE && teamName.trim()
    : entriesOpen && selectedIds.length === ENTRY_SIZE && teamName.trim();

  const playerPanel = (
    <PlayerBrowserPanel
      players={pagedPlayers}
      totalCount={displayedPlayers.length}
      selectedIdsSet={selectedIdsSet}
      selectedIdsLength={selectedIds.length}
      lockedIds={lockedPlayerIds}
      sortKey={sortKey}
      sortDir={sortDir}
      searchQuery={searchQuery}
      page={page}
      totalPages={totalPages}
      // Matching the left column's height only makes sense side by side.
      // Stacked it would inherit the whole form's height; let it size itself.
      height={stackPanel ? undefined : leftColHeight}
      onTogglePlayer={handleTogglePlayer}
      onRemovePlayer={handleRemovePlayer}
      onToggleSort={handleToggleSort}
      onSearchChange={handleSearchChange}
      onPageChange={handlePageChange}
      entriesOpen={entriesOpen}
      editMode={editMode}
    />
  );

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">
      <PageHeader
        title="MLB PLAYOFF CHALLENGE"
        onBack={() => navigate(`/mlb-playoff/current/${numericPoolId}`)}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={8}>
        {/* Always here. The only other way back sat inside Your Entries, so
            until you had drafted a team there was no link at all. */}
        <Flex justify="flex-start" mb={4}>
          <Button
            size="sm"
            bg="white"
            color={TOP_BAR_BLUE}
            _hover={{ bg: "gray.100" }}
            onClick={() => navigate(`/mlb-playoff/current/${numericPoolId}`)}
          >
            Return to Leaderboard
          </Button>
        </Flex>

        {pageError && (
          <Alert status="error" borderRadius="md" mb={6}><AlertIcon />{pageError}</Alert>
        )}
        {justSubmitted && (
          <Alert status="success" borderRadius="md" mb={6}>
            <AlertIcon />
            <strong>{justSubmitted.name}</strong>&nbsp;{justSubmitted.action}! Draft another team below, or&nbsp;
            <Button variant="link" colorScheme="green" size="sm" onClick={() => navigate(`/mlb-playoff/current/${numericPoolId}`)}>
              go to the leaderboard
            </Button>.
          </Alert>
        )}

        {/* Draft Rules */}
        <Box bg="orange.100" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={6} mb={6}>
          <Heading size="lg" color={TOP_BAR_BLUE} mb={4}>Draft Rules</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Box>
              <Text fontWeight="bold" fontSize="md" color={TOP_BAR_BLUE} mb={1}>Roster Construction</Text>
              <VStack align="start" spacing={1}>
                <Text fontSize="sm">· Pick any <strong>{ENTRY_SIZE} hitters</strong> from any eligible playoff team</Text>
                <Text fontSize="sm">· Players are locked once their team plays its first game</Text>
              </VStack>
            </Box>
            <Box>
              <Text fontWeight="bold" fontSize="md" color={TOP_BAR_BLUE} mb={1}>Scoring &amp; Teams</Text>
              <VStack align="start" spacing={1}>
                <Text fontSize="sm">· Each team name must be <strong>unique in the pool</strong></Text>
                <Text fontSize="sm">· You may submit <strong>multiple entries</strong> per pool</Text>
                <Text fontSize="sm">· Single=1, Double=2, Triple=3, HR=4 total bases</Text>
                <Text fontSize="sm">· You can edit any slot whose team hasn't played yet</Text>
              </VStack>
            </Box>
          </SimpleGrid>
        </Box>

        {/* Two-column body */}
        <Flex gap={6} flexWrap={{ base: "wrap", xl: "nowrap" }}>

          {/* LEFT: Draft form + Your Entries */}
          <Box ref={leftColRef} flex="2" minW="300px" maxW={{ base: "100%", xl: "400px" }}>
            <VStack spacing={5} align="stretch">

              {/* Draft form */}
              <Box
                ref={draftFormRef}
                bg="white"
                color="black"
                border={`3px solid ${editMode ? "#b7791f" : TOP_BAR_BLUE}`}
                borderRadius="lg"
                boxShadow="lg"
                p={6}
              >
                <HStack justify="space-between" align="center" mb={4}>
                  <Heading size="md" color={editMode ? "orange.600" : TOP_BAR_BLUE}>
                    {editMode ? "Edit Your Team" : "Draft Your Team"}
                  </Heading>
                  {editMode && (
                    <Button size="xs" variant="ghost" color="gray.500" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  )}
                </HStack>

                {editMode && lockedPlayerIds.size > 0 && (
                  <Alert status="warning" borderRadius="md" mb={4} py={2}>
                    <AlertIcon />
                    <Text fontSize="sm">
                      <strong>{lockedPlayerIds.size} player{lockedPlayerIds.size !== 1 ? "s" : ""}</strong> locked — their team has already played and cannot be swapped.
                    </Text>
                  </Alert>
                )}

                <FormControl mb={4} isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">Team Name</FormLabel>
                  <Input
                    placeholder="Enter a unique team name..."
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    borderColor="gray.300"
                    _focus={{ borderColor: editMode ? "#b7791f" : TOP_BAR_BLUE, boxShadow: `0 0 0 1px ${editMode ? "#b7791f" : TOP_BAR_BLUE}` }}
                    isDisabled={!entriesOpen && !editMode}
                  />
                </FormControl>

                <FormLabel fontSize="sm" fontWeight="semibold" mb={2}>
                  Selected Players ({selectedIds.length}/{ENTRY_SIZE})
                </FormLabel>
                <VStack align="stretch" spacing={1} mb={4}>
                  {Array.from({ length: ENTRY_SIZE }).map((_, i) => {
                    const player  = selectedPlayers[i];
                    const isLocked = player && lockedPlayerIds.has(player.player_id);
                    return (
                      <Flex
                        key={i}
                        align="center"
                        justify="space-between"
                        px={3}
                        py={2}
                        borderRadius="md"
                        bg={player ? (isLocked ? "gray.100" : "blue.50") : "gray.50"}
                        border="1px solid"
                        borderColor={player ? (isLocked ? "gray.300" : "blue.200") : "gray.200"}
                        minH="40px"
                      >
                        {player ? (
                          <>
                            <Box flex="1" minW="0">
                              <HStack spacing={1}>
                                {isLocked && <LockIcon color="gray.400" boxSize={3} />}
                                <Text fontSize="sm" fontWeight="semibold" noOfLines={1} color={isLocked ? "gray.500" : "inherit"}>
                                  {player.name}
                                </Text>
                              </HStack>
                              <Text fontSize="xs" color="gray.500">
                                {teamAbbr(player.team)} · {player.reg_hr ?? 0} HR · {player.reg_avg != null ? player.reg_avg.toFixed(3) : "—"} AVG
                                {isLocked && " · locked"}
                              </Text>
                            </Box>
                            {!isLocked && (
                              <IconButton
                                icon={<CloseIcon boxSize="8px" />}
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                aria-label="Remove player"
                                ml={2}
                                onClick={() => handleRemovePlayer(player.player_id)}
                              />
                            )}
                          </>
                        ) : (
                          <Text fontSize="sm" color="gray.400">Slot {i + 1} — pick from the table</Text>
                        )}
                      </Flex>
                    );
                  })}
                </VStack>

                {submitError && (
                  <Alert status="error" borderRadius="md" mb={3} py={2}>
                    <AlertIcon /><Text fontSize="sm">{submitError}</Text>
                  </Alert>
                )}

                {!entriesOpen && !editMode && (
                  <Alert status="warning" borderRadius="md" mb={3}>
                    <AlertIcon />
                    Entries are closed — all playoff teams have already played their first game.
                  </Alert>
                )}

                <Button
                  w="100%"
                  bg={editMode ? "orange.500" : TOP_BAR_BLUE}
                  color="white"
                  _hover={{ bg: editMode ? "orange.600" : "#00004a" }}
                  isLoading={submitting}
                  loadingText={editMode ? "Saving..." : "Submitting..."}
                  isDisabled={!canSubmit}
                  onClick={handleSubmit}
                >
                  {editMode ? "Save Changes" : "Submit Team"}
                </Button>
              </Box>

              {stackPanel && playerPanel}

              {/* Your submitted entries */}
              {existingEntries.length > 0 && (
                <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={6}>
                  <HStack justify="space-between" mb={3}>
                    <Heading size="sm" color={TOP_BAR_BLUE}>Your Entries ({existingEntries.length})</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    {existingEntries.map((entry) => {
                      const isDeleting   = deletingEntryId === entry.entry_id;
                      const confirming   = confirmDeleteId === entry.entry_id;
                      const isActiveEdit = editEntryId === entry.entry_id;

                      // Can edit if at least one player's team hasn't played yet
                      const hasUnlocked = (entry.players || []).some(
                        (p) => eligiblePlayers.some((ep) => ep.player_id === p.player_id)
                      );

                      return (
                        <Box
                          key={entry.entry_id}
                          p={3}
                          bg={isActiveEdit ? "orange.50" : "green.50"}
                          borderRadius="md"
                          border="1px solid"
                          borderColor={confirming ? "red.300" : isActiveEdit ? "orange.300" : "green.200"}
                        >
                          <HStack justify="space-between" mb={1} align="center">
                            <HStack spacing={2} flex="1" minW="0">
                              <CheckIcon color={isActiveEdit ? "orange.400" : "green.500"} boxSize={3} flexShrink={0} />
                              <Text fontSize="sm" fontWeight="bold" noOfLines={1}>{entry.team_name}</Text>
                              {isActiveEdit && <Badge colorScheme="orange" fontSize="2xs">Editing</Badge>}
                            </HStack>
                            <HStack spacing={1} flexShrink={0}>
                              {!confirming && hasUnlocked && (
                                <Tooltip label="Edit entry" hasArrow>
                                  <IconButton
                                    icon={<EditIcon />}
                                    size="xs"
                                    variant="ghost"
                                    colorScheme={isActiveEdit ? "orange" : "gray"}
                                    aria-label="Edit entry"
                                    onClick={() => handleLoadForEdit(entry)}
                                  />
                                </Tooltip>
                              )}
                              {!confirming && !isActiveEdit && (
                                <Tooltip label="Delete entry" hasArrow>
                                  <IconButton
                                    icon={<DeleteIcon />}
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="red"
                                    aria-label="Delete entry"
                                    onClick={() => setConfirmDeleteId(entry.entry_id)}
                                  />
                                </Tooltip>
                              )}
                            </HStack>
                          </HStack>

                          {confirming && (
                            <HStack mt={2} spacing={2}>
                              <Text fontSize="xs" color="red.600" flex="1">Delete <strong>{entry.team_name}</strong>?</Text>
                              <Button size="xs" colorScheme="red" isLoading={isDeleting}
                                onClick={() => handleDeleteEntry(entry.entry_id)}>Delete</Button>
                              <Button size="xs" variant="ghost"
                                onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
                            </HStack>
                          )}

                          {!confirming && (
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              {(entry.players || []).map((p) => p.name).join(", ")}
                            </Text>
                          )}
                        </Box>
                      );
                    })}
                  </VStack>
                </Box>
              )}
            </VStack>
          </Box>

          {/* RIGHT: Player browser, side by side from xl up */}
          {!stackPanel && playerPanel}

        </Flex>
      </Container>
    </Box>
  );
}
